import { useState, useCallback, useRef } from "react";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

interface UsePaginationProps {
  url: string;
  method?: HttpMethod;
  listKey?: string;
}

type ErrorType = {
  code: number;
  message: string;
  success: boolean;
  data?: {
    message?: string;
    stack?: string;
  };
  error?: {
    error_message?: string;
  };
};

const usePagination = ({ url, method = "POST", listKey  }: UsePaginationProps) => {
  const [data, setData] = useState<any[]>([]);
  const [responseMeta, setResponseMeta] = useState<any>(null); // For non-list metadata (e.g. paymentInfo, balances)
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<ErrorType | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const isFetchingMoreRef = useRef<boolean>(false);
  const nextCursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef<boolean>(false);

  const fetchInitial = useCallback(async (body?: any) => {
    setIsLoading(true);
    setHasFetched(false);
    setError(null);
    setHasMore(false);
    hasMoreRef.current = false;
    setNextCursor(null);
    nextCursorRef.current = null;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }

      const options: RequestInit = {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      let finalUrl = url;
      if (body && (method === "POST" || method === "PUT")) {
        options.body = JSON.stringify(body);
      } else if (body) {
        const params = new URLSearchParams(
          Object.entries(body).reduce((acc: Record<string, string>, [k, v]) => {
            if (v !== undefined && v !== null) acc[k] = String(v);
            return acc;
          }, {})
        );
        finalUrl = url.includes("?") ? `${url}&${params.toString()}` : `${url}?${params.toString()}`;
      }
      const response = await fetch(finalUrl, options);
      const result = await response.json();

      if (response.ok) {
        if (result?.success && result?.data) {
          const items = Array.isArray(result.data)
            ? result.data
            : listKey
              ? (result.data[listKey] ?? [])
              : (Object.values(result.data).find((v) => Array.isArray(v)) as any[]) || [];
          
          const seen = new Set();
          const uniqueItems = items.filter((item: any) => {
            const id = item?.id !== undefined ? item.id : item?._id;
            if (id === undefined || id === null) return true;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });

          setData(uniqueItems);
          setResponseMeta(result.data);
          const next = result.data.pagination?.next_cursor || null;
          const more = result.data.pagination?.has_more || false;
          nextCursorRef.current = next;
          hasMoreRef.current = more;
          setNextCursor(next);
          setHasMore(more);
        }
        return result;
      } else {
        setError(result || { code: response.status, message: "An error occurred" });
        return result;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
      setError({
        code: 500,
        message: errorMessage,
        success: false,
      });
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [url, method, listKey]);

  const fetchMore = useCallback(async (body?: any) => {
    const activeCursor = nextCursorRef.current;
    if (!hasMoreRef.current || isLoadingMore || !activeCursor || isFetchingMoreRef.current) return;
    isFetchingMoreRef.current = true;
    setIsLoadingMore(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }

      const options: RequestInit = {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const requestBody = {
        ...body,
        cursor: activeCursor,
      };

      let finalUrl = url;
      if (method === "POST" || method === "PUT") {
        options.body = JSON.stringify(requestBody);
      } else {
        const params = new URLSearchParams(
          Object.entries(requestBody).reduce((acc: Record<string, string>, [k, v]) => {
            if (v !== undefined && v !== null) acc[k] = String(v);
            return acc;
          }, {})
        );
        finalUrl = url.includes("?") ? `${url}&${params.toString()}` : `${url}?${params.toString()}`;
      }

      const response = await fetch(finalUrl, options);
      const result = await response.json();

      if (response.ok) {
        if (result?.success && result?.data) {
          const items = Array.isArray(result.data)
            ? result.data
            : listKey
              ? (result.data[listKey] ?? [])
              : (Object.values(result.data).find((v) => Array.isArray(v)) as any[]) || [];

          setData((prev) => {
            const existingIds = new Set(
              prev
                .map((item: any) => (item?.id !== undefined ? item.id : item?._id))
                .filter((id: any) => id !== undefined && id !== null)
            );
            if (existingIds.size === 0) {
              return [...prev, ...items];
            }
            const newItems = items.filter((item: any) => {
              const id = item?.id !== undefined ? item.id : item?._id;
              return id === undefined || id === null || !existingIds.has(id);
            });
            return [...prev, ...newItems];
          });
          setResponseMeta(result.data);
          const next = result.data.pagination?.next_cursor || null;
          const more = result.data.pagination?.has_more || false;
          nextCursorRef.current = next;
          hasMoreRef.current = more;
          setNextCursor(next);
          setHasMore(more);
        }
        return result;
      } else {
        setError(result || { code: response.status, message: "An error occurred" });
        return result;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
      setError({
        code: 500,
        message: errorMessage,
        success: false,
      });
    } finally {
      setIsLoadingMore(false);
      isFetchingMoreRef.current = false;
    }
  }, [url, method, listKey, isLoadingMore]);

  const errorContent = error?.message ? (
    <p>
      {error.code === 401 ? (
        <>
          ⚠️ Session expired. Please <a href="/login">login</a> again.
        </>
      ) : error.code === 403 ? (
        <>
          ⚠️ Access Denied.
        </>
      ) : (
        <>{error.message}</>
      )}
    </p>
  ) : null;

  return {
    data,
    setData,
    responseMeta,
    setResponseMeta,
    nextCursor,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    errorContent,
    fetchInitial,
    fetchMore,
    hasFetched,
  };
};

export default usePagination;
